import * as types from './mutation-types'
import Vue from 'vue'
import { getAuth } from '@/services/authService'

export const fetchNotifications = async ({ commit, state, dispatch }) => {
  const auth = getAuth()
  
  if (!auth) {
    commit(types.CLEAR_NOTIFICATIONS)
    return
  }
  
  commit(types.SET_LOADING, true)
  
  try {
    const response = await Vue.prototype.$axios.get('profiles?status=1&complete', { _suppressGlobalLogout: true })
    
    if (response.data && response.data.data) {
      const profiles = response.data.data
      commit(types.SET_PROFILES, profiles)
      
      const notifications = generateNotifications(profiles, state.testMode)
      commit(types.SET_NOTIFICATIONS, notifications)
      commit(types.SET_LAST_FETCH, Date.now())
    }
  } catch (error) {
    console.error('Erro ao buscar notificações:', error)
  } finally {
    commit(types.SET_LOADING, false)
  }
}

export const refreshNotifications = async ({ dispatch }) => {
  await dispatch('fetchNotifications')
}

export const clearNotifications = ({ commit }) => {
  commit(types.CLEAR_NOTIFICATIONS)
}

export const setTestMode = ({ commit, dispatch }, enabled) => {
  commit(types.SET_TEST_MODE, enabled)
  dispatch('fetchNotifications')
}


export const fetchQuotationNotifications = async ({ commit }) => {
  const auth = getAuth()
  
  if (!auth) {
    commit(types.SET_QUOTATION_NOTIFICATIONS, [])
    return
  }
  
  commit(types.SET_QUOTATION_LOADING, true)
  
  try {
    const today = new Date().toISOString().split('T')[0]
    const response = await Vue.prototype.$axios.get(`portal/orders?ignoreProfile=1&groupGrids=1&from=2015-01-01&to=${today}&cdv`, { _suppressGlobalLogout: true })
    
    if (response.data && response.data.data) {
      const grids = response.data.data
      const notifications = generateQuotationNotifications(grids)
      commit(types.SET_QUOTATION_NOTIFICATIONS, notifications)
      commit(types.SET_LAST_QUOTATION_FETCH, Date.now())
    }
  } catch (error) {
    console.error('Erro ao buscar notificações de cotações:', error)
  } finally {
    commit(types.SET_QUOTATION_LOADING, false)
  }
}

export const refreshQuotationNotifications = async ({ dispatch }) => {
  await dispatch('fetchQuotationNotifications')
}

export const fetchAllNotifications = async ({ dispatch }) => {
  await Promise.all([
    dispatch('fetchNotifications'),
    dispatch('fetchQuotationNotifications')
  ])
}

export const refreshAllNotifications = async ({ dispatch }) => {
  await dispatch('fetchAllNotifications')
}

function generateQuotationNotifications(grids) {
  const notifications = []
  
  if (!grids || !Array.isArray(grids)) return notifications
  
  grids.forEach(grid => {
    
    if (!grid.orders || !Array.isArray(grid.orders) || grid.orders.length === 0) return

    
    const firstOrder = grid.orders[0]
    const orderStatusId = parseInt(firstOrder.orderStatusId, 10)
  
    
    if (orderStatusId === 15 && grid.expired === false) {
      notifications.push({
        id: `quotation-${grid.gridId}`,
        type: 'quotation',
        icon: 'mdi-file-document-edit-outline',
        color: 'amber darken-1',
        title: 'Cotação Pendente',
        message: grid.gridName || `Campanha #${grid.gridId}`,
        gridId: grid.gridId,
        route: `/app/comercial/cotacoes/${grid.gridId}`,
        priority: 0
      })
    }
  })
  
  return notifications
}

function generateNotifications(profiles, testMode = false) {
  const notifications = []
  
  Object.keys(profiles).forEach(key => {
    const profile = profiles[key]
    
    if (profile.profileType !== 2) return
    
    const profileName = profile.displayName || profile.name || 'Perfil sem nome'
    const profileId = profile.profileId
    
    const hasAddress = profile.statusAddress && 
                      Array.isArray(profile.statusAddress) && 
                      profile.statusAddress.length > 0
    
    if (!hasAddress || testMode) {
      if (!hasAddress) {
        notifications.push({
          id: `address-${profileId}`,
          type: 'address',
          icon: 'mdi-map-marker-alert-outline',
          color: 'orange',
          title: 'Endereço pendente',
          message: `O perfil "${profileName}" não possui endereço cadastrado. Complete o cadastro para evitar problemas nas operações.`,
          profileId: profileId,
          profileName: profileName,
          route: `/app/management/profiles/${profileId}?tab=address`,
          priority: 2
        })
      }
    }
    
    const cadastral = profile.statusCadastral
    const hasName = cadastral && cadastral.name && cadastral.name.trim() !== ''
    const hasTradingName = cadastral && cadastral.tradingName && cadastral.tradingName.trim() !== ''
    
    if ((!hasName || !hasTradingName) || testMode) {
      if (!hasName || !hasTradingName) {
        let missingField = ''
        if (!hasName && !hasTradingName) {
          missingField = 'Razão Social e Nome Fantasia'
        } else if (!hasName) {
          missingField = 'Razão Social'
        } else {
          missingField = 'Nome Fantasia'
        }
        
        notifications.push({
          id: `cadastral-${profileId}`,
          type: 'cadastral',
          icon: 'mdi-account-alert-outline',
          color: 'red',
          title: 'Dados cadastrais incompletos',
          message: `O perfil "${profileName}" está com ${missingField} pendente. Complete os dados cadastrais.`,
          profileId: profileId,
          profileName: profileName,
          route: `/app/management/profiles/${profileId}?tab=cadastral`,
          priority: 1
        })
      }
    }
    
    const bankAccounts = profile.statusBankAccount
    let hasBankData = false
    
    if (bankAccounts && Array.isArray(bankAccounts) && bankAccounts.length > 0) {
      hasBankData = bankAccounts.some(account => {
        const hasPix = account.pix && account.pix.trim() !== ''
        const hasBankAccount = account.agencyNumber && 
                               account.agencyNumber.trim() !== '' && 
                               account.accountNumber && 
                               account.accountNumber.trim() !== ''
        return hasPix || hasBankAccount
      })
    }
    
    if (!hasBankData || testMode) {
      if (!hasBankData) {
        notifications.push({
          id: `bank-${profileId}`,
          type: 'bank',
          icon: 'mdi-bank-off-outline',
          color: 'amber darken-2',
          title: 'Dados bancários pendentes',
          message: `O perfil "${profileName}" não possui dados bancários ou PIX cadastrado. Adicione para receber pagamentos.`,
          profileId: profileId,
          profileName: profileName,
          route: `/app/management/profiles/${profileId}?tab=bank-data`,
          priority: 3
        })
      }
    }
  })
  
  if (testMode && notifications.length === 0) {
    notifications.push(
      {
        id: 'test-address-1',
        type: 'address',
        icon: 'mdi-map-marker-alert-outline',
        color: 'orange',
        title: 'Endereço pendente (Teste)',
        message: 'Este é um exemplo de notificação de endereço pendente em modo de teste.',
        profileId: 0,
        profileName: 'Perfil de Teste',
        route: '/app/management/profiles',
        priority: 2
      },
      {
        id: 'test-cadastral-1',
        type: 'cadastral',
        icon: 'mdi-account-alert-outline',
        color: 'red',
        title: 'Dados cadastrais incompletos (Teste)',
        message: 'Este é um exemplo de notificação de dados cadastrais em modo de teste.',
        profileId: 0,
        profileName: 'Perfil de Teste',
        route: '/app/management/profiles',
        priority: 1
      },
      {
        id: 'test-bank-1',
        type: 'bank',
        icon: 'mdi-bank-off-outline',
        color: 'amber darken-2',
        title: 'Dados bancários pendentes (Teste)',
        message: 'Este é um exemplo de notificação de dados bancários em modo de teste.',
        profileId: 0,
        profileName: 'Perfil de Teste',
        route: '/app/management/profiles',
        priority: 3
      }
    )
  }
  
  return notifications.sort((a, b) => a.priority - b.priority)
}
